import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, AlertTriangle, Users, Activity, CheckCircle, BarChart3 } from 'lucide-react';
import { api } from '../services/api';
import { ClickableKpiCard } from '../components/common/ClickableKpiCard';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from '../components/ui/table-controls';
import { CHART_COLORS, chartColor, tooltipContentStyle, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState } from '../components/common/States';

const FraudAnalyticsDashboard = () => {
  const [fraudData, setFraudData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metricDetail, setMetricDetail] = useState(null);

  useEffect(() => {
    fetchFraudAnalytics();
  }, []);

  const fetchFraudAnalytics = async () => {
    try {
      setError('');
      const response = await api.get('/api/analytics/fraud-dashboard');
      setFraudData(response.data);
    } catch (error) {
      console.error('Error fetching fraud analytics:', error);
      setError('Unable to load fraud analytics right now. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  const safeFraudData = fraudData || {
    total_active_alerts: 0,
    alerts_by_severity: { critical: 0, high: 0, medium: 0, low: 0 },
    fraud_trend: [],
    risky_employees: [],
  };

  const severityData = [
    { name: 'Critical', value: safeFraudData.alerts_by_severity.critical || 0, color: 'hsl(var(--destructive))' },
    { name: 'High', value: safeFraudData.alerts_by_severity.high || 0, color: 'hsl(var(--chart-1))' },
    { name: 'Medium', value: safeFraudData.alerts_by_severity.medium || 0, color: 'hsl(var(--chart-4))' },
    { name: 'Low', value: safeFraudData.alerts_by_severity.low || 0, color: 'hsl(var(--chart-2))' },
  ];

  const openMetricDetail = (title, value, description, severityKey = null) => {
    setMetricDetail({ title, value, description, severityKey });
  };

  const detailEmployees = (() => {
    if (!metricDetail) return [];
    if (!metricDetail.severityKey) return safeFraudData.risky_employees || [];
    return (safeFraudData.risky_employees || []).filter(
      (employee) => (employee.risk_level || '').toUpperCase() === metricDetail.severityKey
    );
  })();
  const detailEmployeesTable = useTableControls(detailEmployees, {
    initialSortKey: 'fraud_score',
    initialSortDirection: 'desc',
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fraud-analytics-dashboard">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-6 w-6 text-destructive" />
        <h2 className="text-hr-h2 font-bold">Fraud Analytics Dashboard</h2>
      </div>

      {error && (
        <Card className="border-destructive/30 bg-destructive/10">
          <CardContent className="pt-4 flex items-center justify-between gap-3">
            <p className="text-hr-sm text-destructive">{error}</p>
            <button
              type="button"
              className="text-hr-xs px-3 py-1 rounded border border-destructive/40 hover:bg-destructive/10"
              onClick={() => {
                setLoading(true);
                fetchFraudAnalytics();
              }}
            >
              Retry
            </button>
          </CardContent>
        </Card>
      )}

      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-hr-lg">How to use Fraud Dashboard</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-hr-sm text-muted-foreground">
          <p><span className="font-semibold text-foreground">1.</span> Start with <span className="font-semibold text-foreground">Total Active Alerts</span> and severity split to identify urgent risk.</p>
          <p><span className="font-semibold text-foreground">2.</span> Check <span className="font-semibold text-foreground">Fraud Trend</span> to see if incidents are increasing over recent days.</p>
          <p><span className="font-semibold text-foreground">3.</span> Review <span className="font-semibold text-foreground">High-Risk Employees</span> and prioritize CRITICAL/HIGH scores first.</p>
          <p><span className="font-semibold text-foreground">4.</span> Open employee records and fraud alerts from Admin tabs to investigate and close the case.</p>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-4 gap-4">
        <ClickableKpiCard
          title="Total Active Alerts"
          value={safeFraudData.total_active_alerts}
          hint="All unresolved fraud alerts requiring review."
          icon={AlertTriangle}
          iconWrapClassName="p-2 bg-destructive/10 rounded-lg"
          iconClassName="h-4 w-4 text-destructive"
          cardClassName="hover-lift bg-card border-border shadow-sm animate-slide-up"
          valueClassName="text-hr-h2 font-bold text-destructive"
          onClick={() =>
            openMetricDetail(
              'Total Active Alerts',
              safeFraudData.total_active_alerts,
              'This count includes all unresolved fraud flags. Prioritize by severity and assign for investigation.',
              null
            )
          }
        />

        {Object.entries(safeFraudData.alerts_by_severity).map(([severity, count], index) => (
          <ClickableKpiCard
            key={severity}
            title={`${severity} Risk`}
            value={count}
            hint={`Open ${severity.toLowerCase()} severity review context.`}
            icon={AlertTriangle}
            cardClassName="hover-lift shadow-sm animate-slide-up bg-card border-border"
            onClick={() =>
              openMetricDetail(
                `${severity} Risk Alerts`,
                count,
                `${count} unresolved ${severity.toLowerCase()} severity alerts. Review related employees and close validated cases.`,
                severity.toUpperCase()
              )
            }
          />
        ))}
      </div>

      {metricDetail && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between gap-3">
              <CardTitle>{metricDetail.title} - Detailed Page</CardTitle>
              <button
                type="button"
                className="text-hr-xs px-3 py-1 rounded border hover:bg-muted/40"
                onClick={() => setMetricDetail(null)}
              >
                Close
              </button>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            <p className="text-hr-sm text-muted-foreground">{metricDetail.description}</p>
            <div className="rounded-md border overflow-x-auto">
              <table className="w-full text-hr-sm">
                <thead>
                  <tr>
                    <th className="p-2 text-left">Employee</th>
                    <th className="p-2 text-left">Employee ID</th>
                    <th className="p-2 text-left">Risk Level</th>
                    <th className="p-2 text-left">Fraud Score</th>
                  </tr>
                </thead>
                <tbody>
                  {detailEmployeesTable.data.map((employee, idx) => (
                    <tr key={`${employee.employee_id}-${idx}`} className="border-t">
                      <td className="p-2 font-medium">{employee.name}</td>
                      <td className="p-2">{employee.employee_id}</td>
                      <td className="p-2">{employee.risk_level}</td>
                      <td className="p-2">{((employee.fraud_score || 0) * 100).toFixed(0)}%</td>
                    </tr>
                  ))}
                  {detailEmployeesTable.data.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-3 text-center text-muted-foreground">
                        No employees found for this severity.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="pt-3">
              <TableControls
                totalRows={detailEmployeesTable.totalRows}
                page={detailEmployeesTable.page}
                totalPages={detailEmployeesTable.totalPages}
                pageSize={detailEmployeesTable.pageSize}
                pageSizes={detailEmployeesTable.pageSizes}
                onPageSizeChange={detailEmployeesTable.onPageSizeChange}
                onPrevious={detailEmployeesTable.goToPreviousPage}
                onNext={detailEmployeesTable.goToNextPage}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="hover-lift shadow-sm animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <CardHeader>
            <CardTitle>Alerts by Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={severityData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  animationDuration={1500}
                >
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartColor(index)} stroke="none" />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipContentStyle} />
                <Legend layout="vertical" verticalAlign="middle" align="right" />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="hover-lift shadow-sm animate-slide-up" style={{ animationDelay: '0.5s' }}>
          <CardHeader>
            <CardTitle>Fraud Trend (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={safeFraudData.fraud_trend}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" {...axisProps} />
                <YAxis {...axisProps} />
                <Tooltip contentStyle={tooltipContentStyle} />
                <Legend />
                <Line type="monotone" dataKey="count" stroke={CHART_COLORS[0]} strokeWidth={3} dot={{ r: 4, fill: CHART_COLORS[0], strokeWidth: 2, stroke: 'hsl(var(--background))' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-lg hover-lift animate-slide-up" style={{ animationDelay: '0.6s' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            High-Risk Employees
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {safeFraudData.risky_employees.map((emp, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg border transition-all duration-200 hover:shadow-md animate-slide-up ${
                  emp.risk_level === 'CRITICAL' ? 'border-destructive/20 bg-destructive/5 hover:bg-destructive/10' :
                  emp.risk_level === 'HIGH' ? 'border-[hsl(var(--chart-5))]/20 bg-[hsl(var(--chart-5))]/5 hover:bg-[hsl(var(--chart-5))]/10' :
                  'border-[hsl(var(--chart-4))]/20 bg-[hsl(var(--chart-4))]/5 hover:bg-[hsl(var(--chart-4))]/10'
                }`}
                style={{ animationDelay: `${0.7 + (idx * 0.05)}s` }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-foreground">{emp.name}</p>
                    <p className="text-sm text-muted-foreground">{emp.employee_id}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-2 mb-1">
                       <p className={`text-lg font-bold ${
                        emp.risk_level === 'CRITICAL' ? 'text-destructive' :
                        emp.risk_level === 'HIGH' ? 'text-[hsl(var(--chart-5))]' :
                        'text-[hsl(var(--chart-4))]'
                      }`}>
                        {(emp.fraud_score * 100).toFixed(0)}%
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                       emp.risk_level === 'CRITICAL' ? 'bg-destructive/10 text-destructive' :
                       emp.risk_level === 'HIGH' ? 'bg-[hsl(var(--chart-5))]/10 text-[hsl(var(--chart-5))]' :
                       'bg-[hsl(var(--chart-4))]/10 text-[hsl(var(--chart-4))]'
                    }`}>
                      {emp.risk_level}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {safeFraudData.risky_employees.length === 0 && (
              <EmptyState compact title="No high-risk employees detected" description="No employees currently exceed the fraud risk threshold." icon={BarChart3} />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FraudAnalyticsDashboard;
